import { useEffect, useRef, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import AuthGuard from '@/components/AuthGuard';
import { RoleGuard } from '@/components/auth/RoleGuard';
import OfflineIndicator from '@/components/OfflineIndicator';
import { ToastContainer } from '@/components/ui/Toast';
import { useToastStore } from '@/store/toasts';
import { useAuthStore } from '@/store/auth';
import { initPushNotifications, onForegroundMessage } from '@/lib/pushNotifications';
import LoginPage from '@/pages/LoginPage';
import Dashboard from '@/pages/Dashboard';
import HomePage from '@/pages/HomePage';
import OrdersPage from '@/pages/OrdersPage';
import MenuPage from '@/pages/MenuPage';
import ChatPage from '@/pages/ChatPage';
import EarningsPage from '@/pages/EarningsPage';
import AlertsPage from '@/pages/AlertsPage';
import PayoutsPage from '@/pages/PayoutsPage';

function App() {
  // Don't subscribe to auth store here - causes entire app to re-render!
  // AuthGuard will handle authentication checks
  const toasts = useToastStore((state) => state.toasts);
  const removeToast = useToastStore((state) => state.removeToast);
  const addToast = useToastStore((state) => state.addToast);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Frog croak sound for notifications - pre-load on first interaction
  const audioRef = useRef<HTMLAudioElement | null>(null);
  
  const playFrogCroak = useCallback(() => {
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio('/frog-croak.mp3');
        audioRef.current.volume = 0.8; // Louder for vendors - important alerts!
      }
      audioRef.current.currentTime = 0;
      audioRef.current.play().then(() => {
        console.log('🐸 Frog croak played!');
      }).catch(err => {
        console.log('Could not play notification sound:', err);
      });
    } catch (err) {
      console.log('Audio not supported');
    }
  }, []);

  // Pre-load audio on first user interaction
  useEffect(() => {
    const preloadAudio = () => {
      if (!audioRef.current) {
        audioRef.current = new Audio('/frog-croak.mp3');
        audioRef.current.volume = 0.8;
        audioRef.current.load();
        console.log('🐸 Frog croak audio pre-loaded');
      }
    };
    
    document.addEventListener('click', preloadAudio, { once: true });
    document.addEventListener('touchstart', preloadAudio, { once: true });
    
    return () => {
      document.removeEventListener('click', preloadAudio);
      document.removeEventListener('touchstart', preloadAudio);
    };
  }, []);

  // Listen for service worker messages (for background notification sounds)
  useEffect(() => {
    const handleServiceWorkerMessage = (event: MessageEvent) => {
      console.log('Service worker message received:', event.data);
      if (event.data?.type === 'PLAY_NOTIFICATION_SOUND') {
        playFrogCroak();
      }
    };
    
    navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage);
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, [playFrogCroak]);

  // Initialize push notifications when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    initPushNotifications().then((token) => {
      if (token) {
        console.log('Push notifications enabled for vendor');
      }
    });

    // Listen for foreground messages and show toast
    const unsubscribe = onForegroundMessage((payload) => {
      const title = payload.notification?.title || 'New Notification';
      const body = payload.notification?.body || '';
      
      // Play frog croak sound
      playFrogCroak();
      
      addToast({ type: 'info', message: `${title}: ${body}` });
    });

    return unsubscribe;
  }, [isAuthenticated, addToast]);

  return (
    <>
      <BrowserRouter>
        <RoleGuard>
        <Routes>
          {/* Public routes */}
          <Route
            path="/login"
            element={<LoginPage />}
          />

          {/* Protected routes - all nested under Dashboard layout */}
          <Route
            path="/"
            element={
              <AuthGuard>
                <Dashboard />
              </AuthGuard>
            }
          >
            <Route index element={<HomePage />} />
            <Route path="orders" element={<OrdersPage />} />
            <Route path="menu" element={<MenuPage />} />
            <Route path="chat" element={<ChatPage />} />
            <Route path="chat/:threadId" element={<ChatPage />} />
            <Route path="earnings" element={<EarningsPage />} />
            <Route path="alerts" element={<AlertsPage />} />
            <Route path="payouts" element={<PayoutsPage />} />
          </Route>

          {/* Catch-all redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </RoleGuard>
      </BrowserRouter>

      {/* Global offline indicator */}
      <OfflineIndicator />

      {/* Toast notifications - Sprint 4O */}
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </>
  );
}

export default App;

